/**
 * useTabelaPreferencia (Bloco TBL.2).
 *
 * Hook consumer que abstrai PreferenciasTabelasContext para uma tabela
 * especifica. Cada `PainelTabela` chama com seu tableId e recebe:
 *   - widths: dict atual de larguras (vindo do backend ou localStorage legado)
 *   - salvarWidths(novoMap): grava no cache imediato + dispara flush diferido
 *   - estado: 'loading'|'pronto'|'erro_inicial' (caller pode mostrar skeleton)
 *
 * Tambem expõe API UI para componentes globais (TBL.6 PrecosContext):
 *   - ui: dict atual
 *   - setUI(parcial): atualiza chaves seletivamente
 */

import { useCallback, useEffect, useState } from "react";
import { usePreferenciasTabelas } from "../context/PreferenciasTabelasContext";

export default function useTabelaPreferencia(tableId) {
  const {
    getTabelaWidths,
    setTabelaWidths,
    estado,
    versao,
  } = usePreferenciasTabelas();

  // Estado local sincronizado com cache; versao do provider sinaliza
  // mudancas externas (raramente acontece para tabelas mas defensivo).
  const [widths, setWidthsLocal] = useState(() =>
    tableId ? getTabelaWidths(tableId) : {}
  );

  // Re-sincroniza quando cache muda (carregamento inicial concluiu, etc).
  useEffect(() => {
    if (!tableId) return;
    setWidthsLocal(getTabelaWidths(tableId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, versao]);

  const salvarWidths = useCallback(
    (novoMap) => {
      if (!tableId) return;
      setTabelaWidths(tableId, novoMap);
      setWidthsLocal({ ...novoMap });
    },
    [tableId, setTabelaWidths]
  );

  return {
    widths,
    salvarWidths,
    estado,
  };
}

/**
 * Hook irmao para UI compartilhada (precos_solicitados etc).
 * Reage a mudancas via `versao` do provider.
 *
 * `flush` forca o PUT na hora — use apos um setUI disparado por um botao
 * EXPLICITO de salvar ("Salvar visualizacao padrao"), senao a gravacao so
 * sairia no debounce de 2s (ou no unmount do provider, que na pratica nunca
 * acontece porque ele envolve todo o shell autenticado).
 */
export function useTabelaPreferenciaUI() {
  const { getUI, setUIPartial, flush, versao, estado } = usePreferenciasTabelas();
  const [ui, setUIState] = useState(() => getUI());

  useEffect(() => {
    setUIState(getUI());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versao]);

  // `ui` é estado e chega UM RENDER DEPOIS de `estado` virar 'pronto'.
  // Quem precisa do valor no MESMO tick (ex.: hidratar a tela assim que a
  // preferência carrega) deve ler por `getUI()`, que lê o cache do provider.
  return { ui, getUI, setUI: setUIPartial, flush, estado };
}
